import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AbstractMerchantSupplierComponent } from 'app/core/common/abstract.merchant-supplier.component';
import { Event } from 'app/core/models/event';
import { EventService } from 'app/core/services/event.service';
import { MerchantService } from 'app/core/services/merchant.service';
import { NotifMessageService } from 'app/core/services/notifmessage.service';
import { PosService } from 'app/core/services/pos.service';
import { SupplierService } from 'app/core/services/supplier.service';
import { NGXLogger } from 'ngx-logger';
import { combineLatest, forkJoin, of, ReplaySubject, Subscription } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { AuthenticatedRouteGuard } from 'security/authenticatedRouteGuard.service';
import { KeycloakService } from 'security/keycloak.service';
import { tableEventChange } from '../table-utils/classes/tableChangeEvent';
import { columnType } from '../table-utils/enums/columnType';


export class EventData {
  event : Event;
  merchantName : string;
  supplierName : string;
}

@Component({
  selector: 'app-event-page',
  templateUrl: './event-page.component.html'
})
export class EventPageComponent extends AbstractMerchantSupplierComponent implements OnInit, OnDestroy {

  dataSubject$: ReplaySubject<any[]> = new ReplaySubject<any[]>();
  totalLength$: ReplaySubject<number> = new ReplaySubject<number>();

  isLoadingResults : boolean = true;

  events : Map<number, EventData> = new Map<number, EventData>();

  columns: {
    data : {
      title: string;
      type: columnType
    }[],
    clickable : boolean;
    };

  pager : {page : number,
    sort : string,
    size : number
  } = {
    page: 0,
    sort: "createdDate,DESC",
    size: 20
  };

  criteria : Event = new Event();

  columnPropertyMapping = {
    "CREATION_DATE" : "createdDate",
    "EVENT_TYPE" : "eventType",
    "EVENT_SOURCE" : "eventSource",
    "MERCHANT_NAME" : "merchantId",
    "SUPPLIER_NAME" : "supplierId",
    "POS_ID" : "posId",
    "CORRELATION_ID" : "correlationId"
  };

  searchForm : FormGroup = new FormGroup({
    from: new FormControl(),
    to: new FormControl(),
    merchantId: new FormControl(),
    supplierId: new FormControl(),
    merchantRef: new FormControl(),
    supplierRef: new FormControl(),
    posId: new FormControl(),
    eventType: new FormControl(),
    correlationId: new FormControl()
  })

  modalResponseSubscription: Subscription;
  eventType: Array<String> = Array<String>();
  eventTypeMap: Array<{ element: String, traduction: String }> = new Array<{ element: String, traduction: String }>();
  allErrorsMap: Array<{ element: String, traduction: String }> = new Array<{ element: String, traduction: String }>();

  constructor(
    private eventService: EventService,
    protected supplierService: SupplierService,
    protected merchantService: MerchantService,
    protected posService: PosService,
    private translate: TranslateService,
    private notifService: NotifMessageService,
    protected log: NGXLogger,
    public router: Router,
    private keycloakService: KeycloakService,
    private roleGuard: AuthenticatedRouteGuard
  ) { super(supplierService, merchantService, posService, log, router); }


  ngOnInit() {
    this.columns = {
      data : [
      { title: "CREATION_DATE", type: columnType.date },
      { title: "EVENT_TYPE", type: columnType.translateText },
      { title: "EVENT_SOURCE", type: columnType.text },
      { title: "MERCHANT_NAME", type: columnType.text },
      { title: "SUPPLIER_NAME", type: columnType.text },
      { title: "POS_ID", type: columnType.text },
      { title: "CORRELATION_ID", type: columnType.text }
    ],
    clickable : true
    };

    this.criteria.eventCategory = "DEFAULT_EVENT";
    this.getMerchants();
    this.getSuppliers();
    this.eventType = this.getTypeEvent();
    this.sortEventType();

    if (this.eventType != null) {
      this.translate.onLangChange.subscribe(() => {
        this.sortEventType();
      });
    }

    this.keycloakService.rolesObservable.subscribe(roles => {
      if (roles.length !== 0) {
        if (this.roleGuard.isEnabled('EVENT_READ')) {
          this.loadData();
        }
      }
    });
  }

  ngOnDestroy() {
    super.closeSubscribtions();
  }

  formatEvent(event: Event, supplierName : string, merchantName : string) {
    this.events.set(event.id, {event, merchantName, supplierName})
    return {
      id: event.id,
      CREATION_DATE: event.createdDate,
      EVENT_TYPE : event.eventType,
      EVENT_SOURCE : event.eventSource,
      MERCHANT_NAME: merchantName,
      SUPPLIER_NAME: supplierName,
      POS_ID: event.posId,
      CORRELATION_ID: event.correlationId
    };
  }

  loadData(isLoadingSearch : boolean = false) : void{
    this.isLoadingResults = true;
    this.eventService
    .findByCriteria(this.criteria, this.pager)
    .pipe(
      tap(events => this.totalLength$.next(events.totalElements)),
      switchMap(events => {
        if (events.content.length !== 0) {
          const events$ = events.content.map(event => {
            const supplierName$ = this.getSupplierName(event.supplierId);
            const merchantName$ = this.getMerchantName(event.merchantId);
            return forkJoin(([supplierName$, merchantName$])).pipe(
              map(([supplierName, merchantName]) => {
                  return this.formatEvent(event, supplierName, merchantName)
              }
              ));
          });
          return combineLatest(events$)
        }
        if (isLoadingSearch) {
          this.notifService.notifyInfo('EMPTY_RESULT');
        }
        return of([]);
      }),
      map(events => {
        this.isLoadingResults = false;
        if (events === null) {
          return [];
        }
        return events;
      }),
    ).subscribe(events =>  {
      this.dataSubject$.next(events);
    })
  }

  download() {
    this.eventService.findByCriteria(this.criteria, this.pager).subscribe(
      items => {
        if (items.totalElements > 10000) {
          this.notifService.notifyErrorWithoutDetail('EXPORT_NOTE');
        }
        this.eventService.downloadFile(items.content, 'Export_results');
      }
    );
  }

  tableChange(event: tableEventChange) {
    const sortProperty = this.columnPropertyMapping[event.sortEvent.active];
    const sortDirection = event.sortEvent.direction.toUpperCase();
    this.pager = {
      page: event.pageIndex,
      sort: sortDirection !== "" && sortProperty ? [sortProperty, sortDirection].join(",") : "createdDate,DESC",
      size: event.pageSize
    }
    this.loadData();
  }

  search() : void {
    this.criteria = new Event();
    this.criteria.eventCategory = "DEFAULT_EVENT";
    for (let field in this.searchForm.value) {
      if (!this.searchForm.value[field]) {
        delete this.searchForm.value[field];
      } else {
        if (field === "eventType") {
          let trad = this.eventTypeMap.find(Type => Type.traduction == this.searchForm.value[field]);
          this.criteria["eventType"] = (typeof trad !== "undefined") ? trad.element : this.searchForm.value[field];
        } else if (field === "from" || field === "to") {
          const fieldValue = this.searchForm.value[field];
          const date = new Date(fieldValue);
          date.setSeconds(0, 0);
          this.criteria[field] = date.toISOString();
        } else {
          this.criteria[field] = this.searchForm.value[field]}
        }
    }
    if (this.criteria && this.criteria["from"] && this.criteria["to"] && this.criteria["from"] > this.criteria["to"]) {
      this.notifService.notifyErrorWithoutDetail("ERROR_DATE");
      return;
    }
    this.loadData(true);
  }

  details(eventId : number): void {
    const eventData = this.events.get(eventId);
    this.notifService.emitModalWithDetails("EVENT", [eventData.event, eventData.merchantName, eventData.supplierName]);
  }

  clearSearch(): void {
    this.searchForm.reset();
    this.criteria = new Event();
    this.criteria.eventCategory = "DEFAULT_EVENT";
    this.loadData();
  }

  sortEventType() {
    const eventTypes = [];
    this.getTypeEvent().forEach(element =>
      this.translate.get(element).subscribe((trad: string) => {
        eventTypes.push(trad);
        this.eventTypeMap.push({ element: element, traduction: trad });
        eventTypes.sort((a: string, b: string) => {
          return this.translate.instant(a).localeCompare(this.translate.instant(b))
        });
      }));
    this.eventType = eventTypes;
  }
}
